import React, { createContext, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

export const LOCALES = ['en', 'fr', 'es', 'de', 'it', 'pt', 'ar', 'ru', 'zh', 'hi', 'ja'];
export const DEFAULT_LOCALE = 'en';

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  switchLocale: () => {},
  localizedPath: () => '',
  isRTL: false
});

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return context;
};

export const LocaleProvider = ({ children }) => {
  const { locale = DEFAULT_LOCALE } = useParams();
  const navigate = useNavigate();
  
  // Validate locale
  const validLocale = LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
  
  // Check if RTL language
  const isRTL = validLocale === 'ar';
  
  // Function to switch locale
  const switchLocale = (newLocale) => {
    if (!LOCALES.includes(newLocale)) return;
    
    // Get current path without locale prefix
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const currentPath = pathParts.length > 1 ? pathParts.slice(1).join('/') : '';
    
    // Navigate to new locale with same path
    const newPath = `/${newLocale}${currentPath ? '/' + currentPath : ''}`;
    navigate(newPath);
    
    // Update cookie for backend
    document.cookie = `dcf_locale=${newLocale}; path=/; max-age=31536000; SameSite=Lax`;
  };
  
  // Function to generate localized path
  const localizedPath = (path) => {
    // Remove leading slash if present
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `/${validLocale}${cleanPath ? '/' + cleanPath : ''}`;
  };
  
  const value = {
    locale: validLocale,
    switchLocale,
    localizedPath,
    isRTL
  };
  
  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export default LocaleContext;
